import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, ArrowRight, Mail } from "lucide-react";
import { SocialIcon } from "./Brand";
import { SOCIALS } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [topicEmail, setTopicEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ name: "", email: "", message: "" });

  const sendMessage = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axiosPost("/contact", msg);
      toast.success(data.message || "Message sent.");
      setMsg({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axiosPost("/subscribe", { email });
      toast.success(data.message || "You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const pitch = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axiosPost("/pitch", { topic, email: topicEmail });
      toast.success(data.message || "Pitch received.");
      setTopic("");
      setTopicEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16"
        >
          <motion.div variants={fadeUp}>
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
              Stay Plugged In
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Get the <span className="font-garamond italic text-cherry">Decoded Drop.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/65">
              Behind-the-scenes chaos, episode drops and the polls that decide
              next week's topic — straight to your inbox. No spam, we promise.
              Glowgetters get everything first.
            </p>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-white/55">
              Want to book Morgan, collab, or just say what's up? Use the
              message form — it lands straight in her inbox, and she reads
              everything.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.id}
                  data-testid={`contact-social-${s.id}`}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`City Girls Decoded on ${s.name}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-cherry hover:bg-cherry hover:text-white"
                >
                  <SocialIcon id={s.id} />
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-4">
            <form
              data-testid="contact-message-form"
              onSubmit={sendMessage}
              className="rounded-3xl border border-cherry/30 bg-panel p-7 sm:p-9"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cherry">
                Message Morgan
              </p>
              <p className="mt-2 text-xs leading-relaxed text-white/45">
                Goes straight to her inbox — bookings, collabs, questions, all of it.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="contact-name-input"
                  required
                  value={msg.name}
                  onChange={(e) => setMsg({ ...msg, name: e.target.value })}
                  placeholder="Your name"
                  className="h-12 flex-1 rounded-full border border-white/15 bg-ink px-5 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
                />
                <input
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={msg.email}
                  onChange={(e) => setMsg({ ...msg, email: e.target.value })}
                  placeholder="Your email"
                  className="h-12 flex-1 rounded-full border border-white/15 bg-ink px-5 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
                />
              </div>
              <textarea
                data-testid="contact-message-input"
                required
                value={msg.message}
                onChange={(e) => setMsg({ ...msg, message: e.target.value })}
                placeholder="What do you want to get into?"
                rows={3}
                className="mt-3 w-full resize-none rounded-2xl border border-white/15 bg-ink p-4 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
              />
              <button
                data-testid="contact-message-btn"
                type="submit"
                disabled={busy}
                className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-cherry px-6 text-xs font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:scale-[1.02] hover:bg-cherry-soft disabled:opacity-50 sm:w-auto"
              >
                Send it to Morgan <Send className="h-3.5 w-3.5" />
              </button>
            </form>

            <form
              data-testid="newsletter-form"
              onSubmit={subscribe}
              className="rounded-3xl border border-white/10 bg-panel p-7 sm:p-9"
            >
              <label
                htmlFor="newsletter-email"
                className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60"
              >
                Join the Glowgetters
              </label>
              <p className="mt-2 text-xs leading-relaxed text-white/45">
                The email list is only half the membership.{" "}
                <Link
                  to="/glowgetters"
                  data-testid="newsletter-glowgetters-link"
                  className="font-bold text-cherry underline-offset-2 transition-colors hover:text-cherry-soft hover:underline"
                >
                  Dear Glowgetters
                </Link>{" "}
                — Morgan's weekly video letter, a story with the lesson at the
                end. Lives only on this site. Glowgetters only.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  id="newsletter-email"
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@citygirl.com"
                  className="h-12 flex-1 rounded-full border border-white/15 bg-ink px-5 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
                />
                <button
                  data-testid="newsletter-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-cherry px-6 text-xs font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-cherry-soft disabled:opacity-50"
                >
                  Subscribe <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </form>

            <form
              data-testid="pitch-form"
              onSubmit={pitch}
              className="rounded-3xl border border-white/10 bg-panel p-7 sm:p-9"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                Pitch a topic
              </p>
              <p className="mt-2 text-xs leading-relaxed text-white/45">
                Got a situationship or salary drama the girls should decode? Drop it here.
              </p>
              <textarea
                data-testid="pitch-topic-input"
                required
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Spill the topic…"
                rows={3}
                className="mt-4 w-full resize-none rounded-2xl border border-white/15 bg-ink p-4 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
              />
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="pitch-email-input"
                  type="email"
                  value={topicEmail}
                  onChange={(e) => setTopicEmail(e.target.value)}
                  placeholder="Your email (optional)"
                  className="h-12 flex-1 rounded-full border border-white/15 bg-ink px-5 text-sm text-white placeholder:text-white/30 focus:border-cherry focus:outline-none"
                />
                <button
                  data-testid="pitch-topic-btn"
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-6 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-cherry hover:text-cherry disabled:opacity-50"
                >
                  Send it <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

async function axiosPost(path, body) {
  const axios = (await import("axios")).default;
  return axios.post(`${API}/${path}`, body);
}
